/**
 * useDashboardState Hook
 * Manages unified dashboard state (visibility, minimize, opacity, tab selection)
 * Persists preferences to localStorage
 */
import { useState, useEffect, useCallback } from 'react';
const STORAGE_KEY = 'therapist-dashboard-state';
const DEFAULT_STATE = {
    isOpen: false,
    isMinimized: false,
    opacity: 0.95,
    activeTab: 'session'
};
export function useDashboardState() {
    const [state, setState] = useState(() => {
        // Load from localStorage on init, but ALWAYS start closed to ensure button is visible
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                // Force isOpen to false on initial load - user must click to open
                return { ...DEFAULT_STATE, ...parsed, isOpen: false };
            }
        }
        catch (e) {
            console.error('[useDashboardState] Failed to load saved state:', e);
        }
        return DEFAULT_STATE;
    });
    // Save to localStorage whenever state changes
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
        }
        catch (e) {
            console.error('[useDashboardState] Failed to save state:', e);
        }
    }, [state]);
    const toggleOpen = useCallback(() => {
        setState(prev => ({ ...prev, isOpen: !prev.isOpen }));
    }, []);
    const toggleMinimize = useCallback(() => {
        setState(prev => ({ ...prev, isMinimized: !prev.isMinimized }));
    }, []);
    const setOpacity = useCallback((value) => {
        setState(prev => ({ ...prev, opacity: value }));
    }, []);
    const setActiveTab = useCallback((tab) => {
        setState(prev => ({ ...prev, activeTab: tab }));
    }, []);
    return {
        isOpen: state.isOpen,
        isMinimized: state.isMinimized,
        opacity: state.opacity,
        activeTab: state.activeTab,
        toggleOpen,
        toggleMinimize,
        setOpacity,
        setActiveTab
    };
}
